import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
  Stack,
  Chip,
  Switch,
  FormControlLabel,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Card,
  CardContent,
  Divider,
  IconButton,
  Paper,
  Grid,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Info as InfoIcon,
  School as SchoolIcon,
  Build as BuildIcon,
  AttachMoney as MoneyIcon,
  Language as LanguageIcon,
  VideoLibrary as VideoIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckIcon,
  AutoAwesome as AIIcon,
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import {
  Set as SetType,
  setsApi,
  instructionsApi,
  setPartsApi,
  setToolsApi,
} from '../services/api';
import { sanitizeStringOptions, stringOptionLabel } from '../utils/autocompleteOptions';
import { useLanguage } from '../contexts/LanguageContext';
import { normalizeLearningOutcomes } from '../utils/learningOutcomes';
import StepManager from './StepManager';
import AILearningOutcomeGenerator from './AILearningOutcomeGenerator';
import AISetDescriptionHelper from './AISetDescriptionHelper';
import WizardPartsToolsStep, {
  WizardPartSelection,
  WizardToolSelection,
} from './WizardPartsToolsStep';

function mapWizardParts(raw: any[]): WizardPartSelection[] {
  return (raw || [])
    .filter((p) => p && p.part_id != null)
    .map((p: any) => ({
      part_id: Number(p.part_id),
      part_name: p.part_name || p.name || undefined,
      part_number: p.part_number || undefined,
      quantity: Number(p.quantity ?? p.quantity_required ?? 1) || 1,
      is_optional:
        p.is_optional === true ||
        p.is_optional === 1 ||
        p.is_optional === '1' ||
        p.is_required === false ||
        p.is_required === 0,
      notes: p.notes || '',
    }));
}

function mapWizardTools(raw: any[]): WizardToolSelection[] {
  return (raw || [])
    .filter((t) => t && t.tool_id != null)
    .map((t: any) => ({
      tool_id: Number(t.tool_id),
      tool_name: t.tool_name || t.name || undefined,
      tool_number: t.tool_number || undefined,
      quantity: Number(t.quantity ?? 1) || 1,
      is_optional:
        t.is_optional === true ||
        t.is_optional === 1 ||
        t.is_optional === '1' ||
        t.is_required === false ||
        t.is_required === 0,
      notes: t.notes || '',
    }));
}

interface SetFormData {
  category: string;
  difficulty_level: string;
  recommended_age_min: number;
  recommended_age_max: number;
  estimated_duration_minutes: number;
  manual: string;
  build_steps: Array<{
    step_number: number;
    title: string;
    description: string;
    image_url?: string;
  }>;
  base_price: number;
  video_url: string;
  learning_outcomes: string[];
  translations: Array<{
    language_code: string;
    name: string;
    description: string;
  }>;
  parts: WizardPartSelection[];
  tools: WizardToolSelection[];
}

interface SetCreationWizardProps {
  open: boolean;
  onClose: () => void;
  editingSet: SetType | null;
  onSubmit: (formData: SetFormData) => Promise<void>;
  submitting: boolean;
  error: string | null;
  success: string | null;
  availableCategories: string[];
}

const steps = [
  'Basic Information',
  'Target Audience',
  'Parts & Tools',
  'Content & Instructions',
  'Pricing & Media',
  'Translations',
  'Review & Create',
];

function toAiMaterials(parts: WizardPartSelection[], tools: WizardToolSelection[]) {
  return [
    ...parts.map((p) => ({
      name: p.part_name || p.part_number || `Part ${p.part_id}`,
      quantity: p.quantity,
      kind: 'part' as const,
    })),
    ...tools.map((t) => ({
      name: t.tool_name || t.tool_number || `Tool ${t.tool_id}`,
      quantity: t.quantity,
      kind: 'tool' as const,
    })),
  ];
}

const SetCreationWizard: React.FC<SetCreationWizardProps> = ({
  open,
  onClose,
  editingSet,
  onSubmit,
  submitting,
  error,
  success,
  availableCategories,
}) => {
  const { t, currentLanguage } = useLanguage();
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [aiGeneratorOpen, setAiGeneratorOpen] = useState(false);
  const [formData, setFormData] = useState<SetFormData>({
    category: '',
    difficulty_level: '',
    recommended_age_min: 6,
    recommended_age_max: 100,
    estimated_duration_minutes: 120,
    manual: '',
    build_steps: [],
    base_price: 15,
    video_url: '',
    learning_outcomes: [],
    translations: [
      { language_code: 'en', name: '', description: '' },
      { language_code: 'et', name: '', description: '' },
      { language_code: 'ru', name: '', description: '' },
      { language_code: 'fi', name: '', description: '' },
    ],
    parts: [],
    tools: [],
  });

  useEffect(() => {
    if (!open) return;
    setActiveStep(0);
    setCompletedSteps(new Set());
  }, [open, editingSet?.set_id]);

  useEffect(() => {
    if (editingSet) {
      // List rows often only have part_count — hydrate parts/tools below.
      const existingParts = mapWizardParts((editingSet as any).parts || []);
      const existingTools = mapWizardTools((editingSet as any).tools || []);
      setFormData({
        category: editingSet.category || '',
        difficulty_level: editingSet.difficulty_level,
        recommended_age_min: editingSet.recommended_age_min || 6,
        recommended_age_max: editingSet.recommended_age_max || 100,
        estimated_duration_minutes: editingSet.estimated_duration_minutes || 120,
        manual: editingSet.manual || '',
        build_steps: Array.isArray((editingSet as any).build_steps)
          ? (editingSet as any).build_steps
          : [],
        base_price: editingSet.base_price || 0,
        video_url: editingSet.video_url || '',
        learning_outcomes: normalizeLearningOutcomes(editingSet.learning_outcomes),
        translations: [
          { language_code: 'en', name: editingSet.name, description: editingSet.description || '' },
          { language_code: 'et', name: '', description: '' },
          { language_code: 'ru', name: '', description: '' },
          { language_code: 'fi', name: '', description: '' },
        ],
        parts: existingParts,
        tools: existingTools,
      });
    } else {
      setFormData({
        category: '',
        difficulty_level: '',
        recommended_age_min: 6,
        recommended_age_max: 100,
        estimated_duration_minutes: 120,
        manual: '',
        build_steps: [],
        base_price: 15,
        video_url: '',
        learning_outcomes: [],
        translations: [
          { language_code: 'en', name: '', description: '' },
          { language_code: 'et', name: '', description: '' },
          { language_code: 'ru', name: '', description: '' },
          { language_code: 'fi', name: '', description: '' },
        ],
        parts: [],
        tools: [],
      });
    }
  }, [editingSet]);

  // List rows omit build_steps — load instructions when editing so steps reopen.
  // Sets list items do not embed parts[] — load them when editing so Step 3 is not empty.
  useEffect(() => {
    if (!open || !editingSet?.set_id) return;
    let cancelled = false;
    (async () => {
      try {
        const [detailRes, stepsRes, partsRes, toolsRes] = await Promise.all([
          setsApi.getById(editingSet.set_id).catch(() => null),
          instructionsApi.getBySetId(editingSet.set_id).catch(() => ({ data: [] })),
          setPartsApi.getBySetId(editingSet.set_id).catch(() => ({ data: { parts: [] } })),
          setToolsApi.getBySetId(editingSet.set_id).catch(() => ({ data: { tools: [] } })),
        ]);
        if (cancelled) return;
        const detail =
          (detailRes as any)?.data?.set || (detailRes as any)?.data || null;
        const fromDetail = Array.isArray(detail?.build_steps) ? detail.build_steps : null;
        const fromApi = Array.isArray((stepsRes as any)?.data)
          ? (stepsRes as any).data
          : [];
        const rows = fromDetail && fromDetail.length ? fromDetail : fromApi;
        const build_steps = (rows || []).map((row: any, index: number) => ({
          step_number: Number(row.step_order || row.step_number) || index + 1,
          title: row.title || `Step ${index + 1}`,
          description: row.description || row.content || '',
          image_url: row.image_url,
        }));
        const partsRaw =
          (partsRes as any)?.data?.parts ||
          (Array.isArray((partsRes as any)?.data) ? (partsRes as any).data : []) ||
          [];
        const toolsRaw =
          (toolsRes as any)?.data?.tools ||
          (Array.isArray((toolsRes as any)?.data) ? (toolsRes as any).data : []) ||
          [];
        setFormData((prev) => ({
          ...prev,
          manual: detail?.manual != null ? detail.manual : prev.manual,
          build_steps:
            build_steps.length > 0 ? build_steps : prev.build_steps,
          parts: mapWizardParts(partsRaw),
          tools: mapWizardTools(toolsRaw),
        }));
      } catch (err) {
        console.warn('Failed to load set details for edit wizard:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, editingSet?.set_id]);

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 0: // Basic Information
        return !!(formData.category.trim() && formData.difficulty_level.trim());
      case 1: // Target Audience
        return !!(formData.recommended_age_min > 0 && formData.recommended_age_max > 0 && formData.estimated_duration_minutes > 0);
      case 2: // Parts & Tools — require at least one part
        return formData.parts.length > 0;
      case 3: // Content & Instructions
        return true; // Optional step
      case 4: // Pricing & Media
        return !!(formData.base_price && formData.base_price > 0);
      case 5: // Translations
        const englishTranslation = formData.translations.find(t => t.language_code === 'en');
        return !!(englishTranslation?.name.trim());
      case 6: // Review
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setCompletedSteps(prev => new Set([...Array.from(prev), activeStep]));
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleStepClick = (step: number) => {
    if (step <= activeStep || completedSteps.has(step - 1)) {
      setActiveStep(step);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) return;
    try {
      await onSubmit(formData);
    } catch (err) {
      // Parent SetsPage already sets error; keep wizard from swallowing rejection
      console.error('Set create/update failed:', err);
    }
  };

  const isFormValid = () => {
    return (
      validateStep(0) &&
      validateStep(1) &&
      validateStep(2) &&
      validateStep(4) &&
      validateStep(5)
    );
  };

  const getStepIcon = (step: number) => {
    switch (step) {
      case 0: return <InfoIcon />;
      case 1: return <SchoolIcon />;
      case 2: return <InventoryIcon />;
      case 3: return <DescriptionIcon />;
      case 4: return <MoneyIcon />;
      case 5: return <LanguageIcon />;
      case 6: return <CheckIcon />;
      default: return <InfoIcon />;
    }
  };

  const aiMaterials = toAiMaterials(formData.parts, formData.tools);

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon color="primary" />
              Basic Information
            </Typography>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  value={formData.category || ''}
                  onChange={(event, newValue) => {
                    const next =
                      typeof newValue === 'string'
                        ? newValue
                        : (newValue ?? '');
                    setFormData(prev => ({ ...prev, category: next }));
                  }}
                  onInputChange={(event, newInputValue) => {
                    setFormData(prev => ({ ...prev, category: newInputValue }));
                  }}
                  freeSolo
                  options={sanitizeStringOptions(availableCategories)}
                  getOptionLabel={stringOptionLabel}
                  isOptionEqualToValue={(option, value) => option === value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Category *"
                      variant="outlined"
                      required
                      error={!formData.category.trim()}
                      helperText={!formData.category.trim() ? 'Choose or create a category for your set' : 'e.g., Electronics, Woodworking, Robotics'}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              
              <Grid size={{ xs: 12, md: 8 }}>
                <FormControl fullWidth required error={!formData.difficulty_level.trim()}>
                  <InputLabel>Difficulty Level *</InputLabel>
                  <Select
                    value={formData.difficulty_level}
                    onChange={(e) => setFormData(prev => ({ ...prev, difficulty_level: e.target.value }))}
                    label="Difficulty Level *"
                  >
                    <MenuItem value="beginner">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label="Beginner" color="success" size="small" />
                        <Typography>Easy - No prior experience needed</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="intermediate">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label="Intermediate" color="warning" size="small" />
                        <Typography>Moderate - Some experience helpful</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="advanced">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label="Advanced" color="error" size="small" />
                        <Typography>Challenging - Requires experience</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="expert">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label="Expert" color="error" size="small" />
                        <Typography>Professional - Expert level required</Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <SchoolIcon color="primary" />
              Target Audience
            </Typography>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  label="Minimum Age *"
                  type="number"
                  value={formData.recommended_age_min}
                  onChange={(e) => setFormData(prev => ({ ...prev, recommended_age_min: parseInt(e.target.value) || 0 }))}
                  variant="outlined"
                  required
                  error={formData.recommended_age_min <= 0}
                  helperText={formData.recommended_age_min <= 0 ? 'Minimum age is required' : 'Youngest recommended age'}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">👶</InputAdornment>,
                  }}
                />
              </Grid>
              
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  label="Maximum Age *"
                  type="number"
                  value={formData.recommended_age_max}
                  onChange={(e) => setFormData(prev => ({ ...prev, recommended_age_max: parseInt(e.target.value) || 0 }))}
                  variant="outlined"
                  required
                  error={formData.recommended_age_max <= 0}
                  helperText={formData.recommended_age_max <= 0 ? 'Maximum age is required' : 'Oldest recommended age'}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">👴</InputAdornment>,
                  }}
                />
              </Grid>
              
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  label="Duration (minutes) *"
                  type="number"
                  value={formData.estimated_duration_minutes}
                  onChange={(e) => setFormData(prev => ({ ...prev, estimated_duration_minutes: parseInt(e.target.value) || 0 }))}
                  variant="outlined"
                  required
                  error={formData.estimated_duration_minutes <= 0}
                  helperText={formData.estimated_duration_minutes <= 0 ? 'Duration is required' : 'Estimated completion time'}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">⏱️</InputAdornment>,
                  }}
                />
              </Grid>
            </Grid>
            
            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body2">
                <strong>Age Range Tips:</strong> Consider the complexity of assembly, reading requirements, and safety considerations when setting age ranges.
              </Typography>
            </Alert>
          </Box>
        );

      case 2:
        return (
          <WizardPartsToolsStep
            parts={formData.parts}
            tools={formData.tools}
            seedSysComm={!editingSet}
            onPartsChange={(parts) => setFormData((prev) => ({ ...prev, parts }))}
            onToolsChange={(tools) => setFormData((prev) => ({ ...prev, tools }))}
          />
        );

      case 3:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <DescriptionIcon color="primary" />
              Content & Instructions
            </Typography>

            <Alert severity="success" sx={{ mb: 2 }}>
              Generating from {formData.parts.length} part(s) and {formData.tools.length} tool(s)
              selected in the previous step.
            </Alert>
            
            {/* Step-by-Step Instructions */}
            <StepManager
              steps={formData.build_steps}
              onStepsChange={(steps) => {
                setFormData((prev) => ({ ...prev, build_steps: steps }));
              }}
              maxSteps={15}
              setCategory={formData.category}
              setDifficulty={formData.difficulty_level}
              setAgeRange={{ min: formData.recommended_age_min, max: formData.recommended_age_max }}
              setDuration={formData.estimated_duration_minutes}
              setMaterials={aiMaterials}
            />
            
            {/* Additional Manual Content */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <DescriptionIcon color="primary" />
                Additional Instructions & Notes
              </Typography>
              <TextField
                label="Additional Manual Content"
                multiline
                rows={4}
                value={formData.manual}
                onChange={(e) => setFormData(prev => ({ ...prev, manual: e.target.value }))}
                placeholder="Add any additional instructions, safety notes, troubleshooting tips, or other helpful information..."
                variant="outlined"
                fullWidth
                sx={{ mb: 2 }}
                helperText="Saved with the set. Step-by-step steps above are stored separately and reopen in this editor."
              />
            </Box>
            
            {/* Learning Outcomes Section */}
            <Box sx={{ mt: 3, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SchoolIcon color="primary" />
                  Learning Outcomes
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AIIcon />}
                  onClick={() => setAiGeneratorOpen(true)}
                  disabled={!formData.category || !formData.difficulty_level || formData.parts.length === 0}
                >
                  AI Generator
                </Button>
              </Box>
              
              {normalizeLearningOutcomes(formData.learning_outcomes).length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
                  No learning outcomes yet. Use the AI Generator after selecting parts and tools —
                  outcomes are tailored to those materials.
                </Typography>
              ) : (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {normalizeLearningOutcomes(formData.learning_outcomes).length} learning outcome{normalizeLearningOutcomes(formData.learning_outcomes).length !== 1 ? 's' : ''} defined:
                  </Typography>
                  <Stack spacing={1}>
                    {normalizeLearningOutcomes(formData.learning_outcomes).map((outcome, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Typography variant="body2" sx={{ flex: 1, py: 0.5 }}>
                          {index + 1}. {outcome}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => {
                            const updatedOutcomes = normalizeLearningOutcomes(formData.learning_outcomes).filter((_, i) => i !== index);
                            setFormData(prev => ({ ...prev, learning_outcomes: updatedOutcomes }));
                          }}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
              
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Learning Outcomes:</strong> Define what students will learn using the parts
                  and tools in this set. This helps educators see the educational value.
                </Typography>
              </Alert>
            </Box>
            
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Writing Tips:</strong> Reference the selected parts and tools by name. Include
                safety warnings and troubleshooting tips.
              </Typography>
            </Alert>
          </Box>
        );

      case 4:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <MoneyIcon color="primary" />
              Pricing & Media
            </Typography>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Base Price"
                  type="number"
                  inputProps={{ step: "0.01", min: "0.01" }}
                  value={formData.base_price}
                  onChange={(e) => setFormData(prev => ({ ...prev, base_price: parseFloat(e.target.value) || 0 }))}
                  variant="outlined"
                  required
                  error={formData.base_price <= 0}
                  helperText={formData.base_price <= 0 ? 'Price is required' : 'Default €15 — edit if needed'}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  }}
                />
              </Grid>
              
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Video URL"
                  value={formData.video_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, video_url: e.target.value }))}
                  variant="outlined"
                  placeholder="https://youtube.com/watch?v=..."
                  fullWidth
                  helperText="Optional: Add a YouTube video demonstrating the set"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><VideoIcon /></InputAdornment>,
                  }}
                />
              </Grid>
            </Grid>
            
            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body2">
                <strong>Pricing Tips:</strong> Consider material costs, complexity, and market value. Videos help customers understand the product better and increase sales.
              </Typography>
            </Alert>
          </Box>
        );

      case 5:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <LanguageIcon color="primary" />
              Translations
            </Typography>
            
            {formData.translations.map((translation, index) => (
              <Card key={translation.language_code} sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip 
                      label={translation.language_code.toUpperCase()} 
                      color={translation.language_code === 'en' ? 'primary' : 'default'}
                      size="small" 
                    />
                    {translation.language_code === 'en' && <Chip label="Required" color="error" size="small" />}
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        label={`Set Name (${translation.language_code})`}
                        value={translation.name}
                        onChange={(e) => {
                          const newTranslations = [...formData.translations];
                          newTranslations[index].name = e.target.value;
                          setFormData(prev => ({ ...prev, translations: newTranslations }));
                        }}
                        variant="outlined"
                        fullWidth
                        required={translation.language_code === 'en'}
                        error={translation.language_code === 'en' && !translation.name.trim()}
                        helperText={translation.language_code === 'en' ? 'English name is required' : 'Optional translation'}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 1,
                          gap: 1,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Description ({translation.language_code})
                        </Typography>
                        {translation.language_code === 'en' && (
                          <AISetDescriptionHelper
                            name={translation.name}
                            category={formData.category}
                            difficultyLevel={formData.difficulty_level}
                            ageMin={formData.recommended_age_min}
                            ageMax={formData.recommended_age_max}
                            durationMinutes={formData.estimated_duration_minutes}
                            materials={toAiMaterials(formData.parts, formData.tools)}
                            currentDescription={translation.description}
                            onGenerated={(description) => {
                              const newTranslations = [...formData.translations];
                              newTranslations[index] = {
                                ...newTranslations[index],
                                description,
                              };
                              setFormData((prev) => ({
                                ...prev,
                                translations: newTranslations,
                              }));
                            }}
                          />
                        )}
                      </Box>
                      <TextField
                        label={`Description (${translation.language_code})`}
                        multiline
                        rows={3}
                        value={translation.description}
                        onChange={(e) => {
                          const newTranslations = [...formData.translations];
                          newTranslations[index].description = e.target.value;
                          setFormData(prev => ({ ...prev, translations: newTranslations }));
                        }}
                        variant="outlined"
                        fullWidth
                        placeholder="Optional description in this language"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Box>
        );

      case 6:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon color="primary" />
              Review & Create
            </Typography>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Basic Information</Typography>
                    <Typography><strong>Category:</strong> {formData.category}</Typography>
                    <Typography><strong>Difficulty:</strong> {formData.difficulty_level}</Typography>
                    <Typography><strong>Age Range:</strong> {formData.recommended_age_min}-{formData.recommended_age_max} years</Typography>
                    <Typography><strong>Duration:</strong> {formData.estimated_duration_minutes} minutes</Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Materials & Content</Typography>
                    <Typography><strong>Parts:</strong> {formData.parts.length}</Typography>
                    <Typography><strong>Tools:</strong> {formData.tools.length}</Typography>
                    <Typography><strong>Build steps:</strong> {formData.build_steps.length}</Typography>
                    <Typography><strong>Learning outcomes:</strong> {normalizeLearningOutcomes(formData.learning_outcomes).length}</Typography>
                    <Typography><strong>Price:</strong> €{formData.base_price.toFixed(2)}</Typography>
                    <Typography><strong>Video:</strong> {formData.video_url ? 'Added' : 'Not provided'}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Translations</Typography>
                    {formData.translations.map((translation) => (
                      <Box key={translation.language_code} sx={{ mb: 1 }}>
                        <Typography>
                          <strong>{translation.language_code.toUpperCase()}:</strong> {translation.name || 'Not provided'}
                        </Typography>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{ backdrop: { 'aria-hidden': false } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5">
            {editingSet ? 'Edit Set' : 'Create New Set'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Step {activeStep + 1} of {steps.length}
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        {/* Success and Error Messages */}
        {success && (
          <Alert severity="success" sx={{ m: 2 }} onClose={() => {}}>
            {success}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ m: 2 }} onClose={() => {}}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ display: 'flex' }}>
          {/* Stepper */}
          <Box sx={{ width: 300, borderRight: 1, borderColor: 'divider', p: 2 }}>
            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((label, index) => (
                <Step key={label} completed={completedSteps.has(index)}>
                  <StepLabel
                    onClick={() => handleStepClick(index)}
                    sx={{ cursor: 'pointer' }}
                    StepIconComponent={({ active, completed }) => (
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: completed ? 'success.main' : active ? 'primary.main' : 'grey.300',
                          color: 'white',
                          cursor: 'pointer',
                        }}
                      >
                        {completed ? <CheckIcon /> : getStepIcon(index)}
                      </Box>
                    )}
                  >
                    <Typography variant="body2" sx={{ fontWeight: activeStep === index ? 'bold' : 'normal' }}>
                      {label}
                    </Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
          
          {/* Content */}
          <Box sx={{ flex: 1 }}>
            {renderStepContent(activeStep)}
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        
        <Box sx={{ flex: 1 }} />
        
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={submitting}>
            Back
          </Button>
        )}
        
        {activeStep < steps.length - 1 ? (
          <Button 
            onClick={handleNext} 
            variant="contained"
            disabled={!validateStep(activeStep)}
          >
            Next
          </Button>
        ) : (
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={submitting || !isFormValid()}
            startIcon={submitting ? <CircularProgress size={20} /> : undefined}
            sx={{ backgroundColor: 'success.main', '&:hover': { backgroundColor: 'success.dark' } }}
          >
            {submitting 
              ? (editingSet ? 'Updating...' : 'Creating...') 
              : (editingSet ? 'Update Set' : 'Create Set')
            }
          </Button>
        )}
      </DialogActions>
      
      {/* AI Learning Outcome Generator Dialog */}
      <AILearningOutcomeGenerator
        open={aiGeneratorOpen}
        onClose={() => setAiGeneratorOpen(false)}
            onApply={(outcomes: string[]) => {
          setFormData(prev => ({ ...prev, learning_outcomes: outcomes }));
          setAiGeneratorOpen(false);
        }}
        currentOutcomes={normalizeLearningOutcomes(formData.learning_outcomes)}
        setCategory={formData.category}
        setDifficulty={formData.difficulty_level}
        setAgeRange={{ min: formData.recommended_age_min, max: formData.recommended_age_max }}
        setDuration={formData.estimated_duration_minutes}
        setMaterials={aiMaterials}
      />
    </Dialog>
  );
};

export default SetCreationWizard;
